import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Layers, Loader2, Play, AlertTriangle } from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { VerdictBadge } from '@/components/wallet-intel/VerdictBadge';
import { AdmiraltyBadge } from '@/components/wallet-intel/AdmiraltyBadge';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';
import { useBackgroundJobs } from '@/contexts/BackgroundJobsContext';

const CHAINS = [
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'tron', label: 'Tron' },
];

function parseAddresses(raw: string, defaultChain: string): { chain: string; address: string }[] {
  return raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 100)
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length === 2) return { chain: parts[0], address: parts[1] };
      return { chain: defaultChain, address: line };
    });
}

function shorten(addr: string): string {
  if (addr.length <= 18) return addr;
  return `${addr.slice(0, 10)}...${addr.slice(-6)}`;
}

export default function BatchScreening() {
  const [raw, setRaw] = useState('');
  const [defaultChain, setDefaultChain] = useState('ethereum');
  const [includeExposure, setIncludeExposure] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Apply this analyst's saved defaults (Settings page) once, before they've
  // touched either control themselves.
  const settingsQuery = useQuery({
    queryKey: ['wallet-intel-settings'],
    queryFn: () => walletIntelApi.getSettings(),
  });
  useEffect(() => {
    if (settingsQuery.data && !defaultsApplied) {
      setDefaultChain(settingsQuery.data.default_chain);
      setIncludeExposure(settingsQuery.data.include_exposure_by_default);
      setDefaultsApplied(true);
    }
  }, [settingsQuery.data, defaultsApplied]);

  const items = parseAddresses(raw, defaultChain);
  const { runJob } = useBackgroundJobs();

  const mutation = useMutation({
    // Wrapped in runJob so this keeps running (and shows up in the top-bar
    // indicator) even if the analyst navigates to another page mid-batch —
    // previously, leaving this page killed the in-flight request's visible
    // state entirely with no way to tell it was still going or had finished.
    // No `href` here: unlike a SAR export PDF, a batch result has nowhere to
    // persist once this page unmounts, so the summary line is the only trace
    // of the outcome if you've already navigated away — that's disclosed
    // honestly rather than linking back to what would just be a blank page.
    mutationFn: () => runJob(`Batch screening ${items.length} address${items.length === 1 ? '' : 'es'}`, () =>
      walletIntelApi.screenBatch({
        items, include_exposure: includeExposure,
        max_hops: settingsQuery.data?.default_max_hops,
        max_fanout: settingsQuery.data?.default_max_fanout,
      }),
      { summary: (result) => `${result.total} screened, ${result.flagged_count} flagged` },
    ),
  });

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }} className="px-6 py-4 mb-6">
        <h1 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>Batch Screening</h1>
        <p style={{ color: '#8D96AC', fontSize: 13, marginTop: 3 }}>
          Screen up to 100 addresses at once against the blocklist and live sanctions data
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Input panel */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: 20 }}
          className="lg:col-span-1 flex flex-col gap-4"
        >
          <div>
            <p className="tracking-[0.1em] mb-2.5" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>
              ADDRESSES (one per line, optionally "chain,address")
            </p>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={'0x000000000000000000000000000000000000800a\nbitcoin,175tWpb8K1Y1fv4DgA7hbyzKR7uz9ARfBs\n...'}
              rows={12}
              className="w-full rounded-lg px-3 py-2.5 font-mono outline-none resize-none"
              style={{ background: '#050709', border: '1px solid #182030', color: '#DCE8F5', fontSize: 12.5 }}
            />
          </div>

          <div className="flex items-center justify-between">
            <span style={{ color: '#8D96AC', fontSize: 12.5 }}>Default chain</span>
            <select
              value={defaultChain}
              onChange={(e) => setDefaultChain(e.target.value)}
              className="rounded-lg px-2.5 py-1.5 outline-none"
              style={{ background: '#050709', border: '1px solid #182030', color: '#8D96AC', fontSize: 12.5, fontWeight: 600 }}
            >
              {CHAINS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeExposure} onChange={(e) => setIncludeExposure(e.target.checked)} />
            <span style={{ color: '#8D96AC', fontSize: 12.5 }}>Include exposure check (slower, more checks per address)</span>
          </label>

          <div style={{ color: '#5A7A9A', fontSize: 11.5 }}>
            {items.length} address{items.length === 1 ? '' : 'es'} parsed
            {items.length >= 100 && ' (capped at 100)'}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => mutation.mutate()}
            disabled={items.length === 0 || mutation.isPending}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontSize: 13 }}
          >
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            RUN BATCH SCREEN
          </motion.button>

          {mutation.isError && (
            <div className="flex items-center gap-2" style={{ color: '#FF1F1F' }}>
              <AlertTriangle size={14} />
              <span style={{ fontSize: 12 }}>{(mutation.error as Error)?.message}</span>
            </div>
          )}
        </motion.div>

        {/* Results panel */}
        <div className="lg:col-span-2">
          {!mutation.data && !mutation.isPending && (
            <div style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: '64px 32px', textAlign: 'center' }}>
              <Layers size={26} style={{ color: '#3A5A7A', margin: '0 auto 12px' }} />
              <p style={{ color: '#8D96AC', fontSize: 13.5 }}>Results will appear here once you run a batch.</p>
            </div>
          )}

          {mutation.isPending && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="animate-spin" size={22} style={{ color: '#00C8FF' }} />
            </div>
          )}

          {mutation.data && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <motion.div whileHover={{ y: -2 }} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 22 }}>{mutation.data.total}</div>
                  <div style={{ color: '#7C8CA6', fontSize: 11, marginTop: 3, fontWeight: 600 }}>TOTAL SCREENED</div>
                </motion.div>
                <motion.div whileHover={{ y: -2 }} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ color: '#FF1F1F', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 22 }}>{mutation.data.flagged_count}</div>
                  <div style={{ color: '#7C8CA6', fontSize: 11, marginTop: 3, fontWeight: 600 }}>FLAGGED</div>
                </motion.div>
                <motion.div whileHover={{ y: -2 }} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ color: '#F0B429', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 22 }}>{mutation.data.error_count}</div>
                  <div style={{ color: '#7C8CA6', fontSize: 11, marginTop: 3, fontWeight: 600 }}>INVALID ADDRESS</div>
                </motion.div>
              </div>

              <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, overflow: 'hidden' }}>
                <div className="grid px-4 py-3" style={{ gridTemplateColumns: '1fr 90px 100px 80px', borderBottom: '1px solid #0F1520' }}>
                  {['ADDRESS', 'CHAIN', 'VERDICT', 'GRADE'].map((h) => (
                    <span key={h} style={{ color: '#5A7A9A', fontSize: 10.5, letterSpacing: '0.06em', fontWeight: 600 }}>{h}</span>
                  ))}
                </div>
                {mutation.data.results.map((r, i) => (
                  <div
                    key={`${r.chain}-${r.address}-${i}`}
                    className="grid px-4 py-3.5 items-center"
                    style={{ gridTemplateColumns: '1fr 90px 100px 80px', borderBottom: '1px solid #0F1520' }}
                  >
                    <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12 }} title={r.address}>
                      {shorten(r.address)}
                      {r.error && <span style={{ color: '#FF1F1F', display: 'block', fontSize: 11, marginTop: 3 }}>{r.error}</span>}
                    </span>
                    <span style={{ color: '#8D96AC', fontSize: 12 }}>{r.chain}</span>
                    <VerdictBadge verdict={r.verdict} size="sm" />
                    {r.admiralty_grade ? <AdmiraltyBadge grade={r.admiralty_grade} compact /> : <span style={{ color: '#3A5A7A', fontSize: 12 }}>—</span>}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
